import { useEffect, useState } from "react";
import { api, errMsg, fileUrl } from "@/lib/api";
import { EmptyState, PageHeader, Spinner } from "@/components/bits";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Download, Eye, Megaphone } from "lucide-react";
import { toast } from "sonner";

function inValidity(n) {
  const now = new Date();
  return new Date(n.valid_from) <= now && now <= new Date(n.valid_until);
}

export default function ClientNoticesPage() {
  const [notices, setNotices] = useState(null);
  const [viewing, setViewing] = useState(null);

  useEffect(() => {
    api.get("/tenant/notices/board")
      .then(({ data }) => setNotices(data.filter(inValidity)))
      .catch(() => setNotices([]));
  }, []);

  const downloadImage = async (n) => {
    try {
      const { data } = await api.get(`/files/${n.image_path}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `aviso-${n.title.replace(/\s+/g, "-").toLowerCase()}.png`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  if (!notices) return <Spinner />;

  return (
    <div data-testid="client-notices-page">
      <PageHeader title="Mural de Avisos" subtitle="Comunicados vigentes da sua organização" />

      {notices.length === 0 ? (
        <EmptyState icon={Megaphone} title="Nenhum aviso no momento"
          subtitle="Quando houver comunicados vigentes, eles aparecerão aqui." />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {notices.map((n) => (
            <div key={n.id} data-testid={`client-notice-card-${n.id}`}
              className="bg-zinc-900 border border-zinc-800 rounded-md overflow-hidden flex flex-col">
              {n.image_mode === "include" && n.image_path && (
                <div className="aspect-[4/3] bg-zinc-950 flex items-center justify-center">
                  <img src={fileUrl(n.image_path)} alt={n.title} className="max-w-full max-h-full object-contain" />
                </div>
              )}
              <div className="p-4 flex flex-col gap-2 flex-1">
                <h3 className="text-sm font-medium text-zinc-100" data-testid={`client-notice-title-${n.id}`}>{n.title}</h3>
                <p className="text-xs text-zinc-400 line-clamp-3 whitespace-pre-wrap flex-1">{n.text}</p>
                <Button variant="ghost" size="sm" data-testid={`client-notice-view-${n.id}`} onClick={() => setViewing(n)}
                  className="self-start text-indigo-400 hover:text-indigo-300 hover:bg-zinc-800 -ml-2">
                  <Eye className="h-4 w-4 mr-1.5" /> Ver aviso
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!viewing} onOpenChange={(v) => !v && setViewing(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100" data-testid="client-notice-view-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-medium">Aviso</DialogTitle>
            <DialogDescription className="text-sm text-zinc-400">
              {viewing?.image_mode === "download" ? "Imagem disponível para download." : "Visualização do comunicado."}
            </DialogDescription>
          </DialogHeader>
          {viewing && viewing.image_mode === "download" ? (
            <div className="space-y-4">
              <h3 className="text-base font-medium text-zinc-100" data-testid="client-notice-view-title">{viewing.title}</h3>
              <p className="text-sm text-zinc-300 whitespace-pre-wrap" data-testid="client-notice-view-text">{viewing.text}</p>
              {viewing.image_path && (
                <Button data-testid="client-notice-download-image-button" onClick={() => downloadImage(viewing)}
                  className="bg-indigo-600 hover:bg-indigo-500 text-white rounded-md transition-colors duration-200">
                  <Download className="h-4 w-4 mr-1.5" /> Baixar Imagem
                </Button>
              )}
            </div>
          ) : viewing && (
            <div className="mx-auto w-full max-w-[300px] rounded-2xl border border-zinc-700 bg-zinc-950 shadow-xl shadow-black/50 overflow-hidden"
              data-testid="client-notice-view-mobile-card">
              {viewing.image_path && (
                <div className="w-full aspect-square bg-zinc-900 flex items-center justify-center">
                  <img src={fileUrl(viewing.image_path)} alt={viewing.title} data-testid="client-notice-view-image"
                    className="max-w-full max-h-full object-contain" />
                </div>
              )}
              <div className="p-4 space-y-2">
                <h3 className="text-base font-medium text-zinc-100" data-testid="client-notice-view-title">{viewing.title}</h3>
                <p className="text-sm text-zinc-300 whitespace-pre-wrap" data-testid="client-notice-view-text">{viewing.text}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
